import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from "recharts";
import axios from "../utils/axios";
import { getAuth } from "firebase/auth";


function fmt(d) {
  return d.toISOString().slice(0, 10);
}


export default function BiDashboard() {
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 14);
    return fmt(d);
  });
  const [to, setTo] = useState(() => fmt(new Date()));

  const [daily, setDaily] = useState([]);
  const [byName, setByName] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagesDaily, setPagesDaily] = useState([]);
  const [pagesTop, setPagesTop] = useState([]);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null);

  const totals = useMemo(() => {
  return daily.reduce(
    (acc, r) => ({
      events: acc.events + Number(r.events || 0),
      sessions: acc.sessions + Number(r.sessions || 0),
      users: acc.users + Number(r.users || 0),
      users_identified: acc.users_identified + Number(r.users_identified || 0),
      users_anonymous: acc.users_anonymous + Number(r.users_anonymous || 0),
    }),
    {
      events: 0,
      sessions: 0,
      users: 0,
      users_identified: 0,
      users_anonymous: 0,
    }
  );
}, [daily]);

  const [campaigns, setCampaigns] = useState([]);
const [campaignId, setCampaignId] = useState("");
const [campaignFunnel, setCampaignFunnel] = useState([]);
const [funnelDaily, setFunnelDaily] = useState([]);
const [drill, setDrill] = useState([]);
const [journey, setJourney] = useState([]);
const [journeyOpen, setJourneyOpen] = useState(false);

async function fetchCampaigns() {
  try {
    const headers = await authHeaders();
    const res = await axios.get(
      `https://modularobjects.com/marketing/api/bi/campaigns?from=${from}&to=${to}`,
      { headers }
    );
    setCampaigns(res.data || []);
  } catch (e) {
    console.error("fetchCampaigns failed:", e?.response?.data || e);
    setCampaigns([]);
  }
}

async function fetchCampaignFunnel() {
  try {
    const headers = await authHeaders();
    const q = campaignId ? `&campaign_id=${campaignId}` : "";
    const res = await axios.get(
      `https://modularobjects.com/marketing/api/bi/campaign-funnel-daily?from=${from}&to=${to}${q}`,
      { headers }
    );
    setCampaignFunnel(res.data || []);
  } catch (e) {
    console.error("fetchCampaignFunnel failed:", e?.response?.data || e);
    setCampaignFunnel([]);
  }
}

const generateColor = (index) => {
  const hue = (index * 137.508) % 360; // garant različite
  return `hsl(${hue}, 75%, 55%)`;
};

// ako želiš da biraš fazu, ostavi ovo; ako ne, setuj stalno "landing_users"
const [stage, setStage] = useState("landing_users");

const campaignList = useMemo(() => {
  const set = new Set();
  campaignFunnel.forEach(r => set.add(String(r.campaign_id ?? "unknown")));
  return Array.from(set);
}, [campaignFunnel]);

const pivotedCampaignData = useMemo(() => {
  const map = {};

  campaignFunnel.forEach((row) => {
    const date = row.event_date;
    const campaign = String(row.campaign_id ?? "unknown");
    if (!date) return;

    if (!map[date]) map[date] = { event_date: date };

    const v = Number(row[stage] ?? 0);
    // sum (sigurno ako ima duplih redova)
    map[date][campaign] = (Number(map[date][campaign]) || 0) + v;
  });

  return Object.values(map).sort((a,b)=>a.event_date.localeCompare(b.event_date));
}, [campaignFunnel, stage]);

async function fetchCampaignDrilldown() {
  if (!campaignId) {
    setDrill([]);
    return;
  }

  try {
    const headers = await authHeaders();
    const res = await axios.get(
      `https://modularobjects.com/marketing/api/bi/campaign-drilldown?from=${from}&to=${to}&campaign_id=${campaignId}`,
      { headers }
    );
    setDrill(res.data || []);
  } catch (e) {
    console.error("fetchCampaignDrilldown failed:", e?.response?.data || e);
    setDrill([]);
  }
}

async function openJourney({ contact_id, offer_id }) {
  try {
    const headers = await authHeaders();
    const q = `contact_id=${contact_id || ""}&offer_id=${offer_id || ""}`;
    const res = await axios.get(
      `https://modularobjects.com/marketing/api/bi/journey?from=${from}&to=${to}&${q}`,
      { headers }
    );
    setJourney(res.data || []);
    setJourneyOpen(true);
  } catch (e) {
    console.error("openJourney failed:", e?.response?.data || e);
    setJourney([]);
    setJourneyOpen(false);
  }
}


useEffect(()=>{ fetchCampaigns(); }, [from,to]);
useEffect(()=>{ fetchCampaignFunnel(); fetchCampaignDrilldown(); }, [from,to,campaignId]);

  async function authHeaders() {
  const auth = getAuth();
  const u = auth.currentUser;
  const token = u ? await u.getIdToken() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function loadRefreshStatus() {
  try {
    const headers = await authHeaders();
    const res = await axios.get(
      "https://modularobjects.com/marketing/api/bi/refresh/status",
      { headers }
    );

    setRefreshStatus(res.data);
    setRefreshing(!!res.data?.running);
  } catch (e) {
    console.error("loadRefreshStatus failed:", e?.response?.data || e);

    setRefreshStatus({
      status: "error",
      running: false,
      startedAt: null,
      finishedAt: null,
      exitCode: null,
      logTail: JSON.stringify(e?.response?.data || e.message || e, null, 2),
    });

    setRefreshing(false);
  }
}


  useEffect(() => {
  loadRefreshStatus();
  // polling dok radi
  const t = setInterval(() => {
    if (refreshing) loadRefreshStatus();
  }, 2000);
  return () => clearInterval(t);
}, [refreshing]);

  useEffect(() => {
  (async () => {
    setLoading(true);

    try {
      const headers = await authHeaders();

      const results = await Promise.allSettled([
        axios.get(`https://modularobjects.com/marketing/api/bi/events-daily?from=${from}&to=${to}`, { headers }),
        axios.get(`https://modularobjects.com/marketing/api/bi/events-by-name?from=${from}&to=${to}`, { headers }),
        axios.get(`https://modularobjects.com/marketing/api/bi/pages-daily?from=${from}&to=${to}`, { headers }),
        axios.get(`https://modularobjects.com/marketing/api/bi/pages-top?from=${from}&to=${to}`, { headers }),
      ]);

      const [dRes, nRes, pRes, ptRes] = results;

      if (dRes.status === "fulfilled") {
        setDaily(dRes.value.data || []);
      } else {
        console.error("events-daily failed:", dRes.reason?.response?.data || dRes.reason);
        setDaily([]);
      }

      if (nRes.status === "fulfilled") {
        setByName(nRes.value.data || []);
      } else {
        console.error("events-by-name failed:", nRes.reason?.response?.data || nRes.reason);
        setByName([]);
      }

      if (pRes.status === "fulfilled") {
        setPagesDaily(pRes.value.data || []);
      } else {
        console.error("pages-daily failed:", pRes.reason?.response?.data || pRes.reason);
        setPagesDaily([]);
      }

      if (ptRes.status === "fulfilled") {
        setPagesTop(ptRes.value.data || []);
      } else {
        console.error("pages-top failed:", ptRes.reason?.response?.data || ptRes.reason);
        setPagesTop([]);
      }
    } finally {
      setLoading(false);
    }
  })();
}, [from, to]);

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-3xl font-bold text-primary-light dark:text-primary-dark">
            📊 BI Dashboard
          </h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            Data source: BigQuery (fact_*)
          </p>
        </div>

        <div className="flex gap-2 items-end">
          <div>
            <div className="text-xs mb-1">From</div>
            <input className="border rounded px-2 py-1 dark:bg-neutral-800" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <div className="text-xs mb-1">To</div>
            <input className="border rounded px-2 py-1 dark:bg-neutral-800" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="flex gap-2 items-center">
  {/* <button
    onClick={runRefresh}
    disabled={refreshing}
    className={`px-3 py-2 rounded text-sm font-semibold ${
      refreshing ? "bg-gray-300 cursor-not-allowed" : "bg-primary text-white hover:bg-primary-dark"
    }`}
  >
    {refreshing ? "Refreshing..." : "🔄 Refresh data"}
  </button> */}

  <button
    onClick={loadRefreshStatus}
    className="px-3 py-2 rounded text-sm border dark:border-neutral-700"
  >
    Status
  </button>
</div>

{refreshStatus && (
  <div className="mt-3 border rounded p-3 text-sm dark:border-neutral-700 dark:bg-neutral-900">
    <div className="flex flex-wrap gap-3 items-center">
      <div><b>Status:</b> {refreshStatus.running ? "running" : refreshStatus.status}</div>
      <div><b>Started:</b> {refreshStatus.startedAt || "-"}</div>
      <div><b>Finished:</b> {refreshStatus.finishedAt || "-"}</div>
      <div><b>Exit:</b> {String(refreshStatus.exitCode ?? "-")}</div>
    </div>

    <div className="mt-2">
      <div className="opacity-70 mb-1">Log (tail)</div>
      <pre className="max-h-56 overflow-auto text-xs p-2 rounded bg-black text-white">
        {refreshStatus.logTail || ""}
      </pre>
    </div>
  </div>
)}

<div className="border rounded p-4 h-[340px] dark:border-neutral-700 dark:bg-neutral-900">
  <div className="flex items-center gap-2 mb-2">
  <div className="font-semibold">Campaign funnel (Users)</div>

  {/* opciono: biraš funnel fazu */}
  <select
    className="ml-auto border rounded px-2 py-1 dark:bg-neutral-800"
    value={stage}
    onChange={(e) => setStage(e.target.value)}
  >
    <option value="landing_users">Landing</option>
    <option value="interest_users">Interest</option>
    <option value="intent_users">Intent</option>
    <option value="conversion_users">Conversion</option>
  </select>
</div>

<ResponsiveContainer width="100%" height="100%">
  <LineChart data={pivotedCampaignData}>
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis dataKey="event_date" />
    <YAxis />
    <Tooltip />
    <Legend />

    {campaignList.map((cid) => (
      <Line
        key={cid}
        type="monotone"
        dataKey={cid}               // 👈 svaka kampanja je posebna kolona u pivoted data
        stroke={generateColor(cid)} // 👈 posebna boja po kampanji
        strokeWidth={2}
        dot={false}
      />
    ))}
  </LineChart>
</ResponsiveContainer>
</div>

<div className="border rounded p-4 dark:border-neutral-700 dark:bg-neutral-900">
  <div className="font-semibold mb-3">Campaign drilldown</div>
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead className="text-left opacity-70">
        <tr>
          <th className="py-2 pr-4">contact_id</th>
          <th className="py-2 pr-4">offer_id</th>
          <th className="py-2 pr-4">sessions</th>
          <th className="py-2 pr-4">events</th>
          <th className="py-2 pr-4">first</th>
          <th className="py-2 pr-4">last</th>
        </tr>
      </thead>

      <tbody>
        {drill.map((r, idx)=>(
          <tr
            key={idx}
            className="border-t dark:border-neutral-800 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800"
            onClick={()=>openJourney({contact_id:r.contact_id, offer_id:r.offer_id})}
          >
            <td className="py-2 pr-4">{r.contact_id ?? "-"}</td>
            <td className="py-2 pr-4">{r.offer_id ?? "-"}</td>
            <td className="py-2 pr-4">{r.sessions}</td>
            <td className="py-2 pr-4">{r.events}</td>
            <td className="py-2 pr-4">{r.first_ts}</td>
            <td className="py-2 pr-4">{r.last_ts}</td>
          </tr>
        ))}
        {!drill.length && <tr><td colSpan={6} className="py-3 opacity-70">No data</td></tr>}
      </tbody>
    </table>
  </div>
</div>

<div className="flex gap-2 items-end">
 <div>
  <div className="text-xs mb-1">Campaign</div>
  <select
    className="border rounded px-2 py-1 dark:bg-neutral-800"
    value={campaignId}
    onChange={(e)=>setCampaignId(e.target.value)}
  >
    <option value="">All</option>
    {campaigns.map(c => (
      <option key={c.campaign_id} value={String(c.campaign_id)}>
        {c.campaign_id} ({c.tokens} tokens)
      </option>
    ))}
  </select>
</div>
</div>

<div className="border rounded p-4 dark:border-neutral-700 dark:bg-neutral-900">
  <div className="font-semibold mb-3">Campaign funnel (Users)</div>
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead className="text-left opacity-70">
        <tr>
          <th className="py-2 pr-4">date</th>
          <th className="py-2 pr-4">campaign_id</th>
          <th className="py-2 pr-4">landing</th>
          <th className="py-2 pr-4">interest</th>
          <th className="py-2 pr-4">intent</th>
          <th className="py-2 pr-4">conversion</th>
          <th className="py-2 pr-4">CVR</th>
        </tr>
      </thead>
      <tbody>
        {campaignFunnel.map((r, idx) => {
          const l = Number(r.landing_users||0);
          const c = Number(r.conversion_users||0);
          const cvr = l ? ((c/l)*100).toFixed(1) + "%" : "—";
          return (
            <tr key={`${r.event_date}-${r.campaign_id}-${idx}`} className="border-t dark:border-neutral-800">
              <td className="py-2 pr-4">{r.event_date}</td>
              <td className="py-2 pr-4">{r.campaign_id}</td>
              <td className="py-2 pr-4">{r.landing_users}</td>
              <td className="py-2 pr-4">{r.interest_users}</td>
              <td className="py-2 pr-4">{r.intent_users}</td>
              <td className="py-2 pr-4">{r.conversion_users}</td>
              <td className="py-2 pr-4">{cvr}</td>
            </tr>
          );
        })}
        {!campaignFunnel.length && (
          <tr><td className="py-3 opacity-70" colSpan={7}>No data</td></tr>
        )}
      </tbody>
    </table>
  </div>
</div>

      {/* KPI */}
      <div className="grid gap-3 sm:grid-cols-5">
  <div className="border rounded p-4 dark:border-neutral-700 dark:bg-neutral-900">
    <div className="text-sm opacity-70">Events</div>
    <div className="text-2xl font-bold">{totals.events}</div>
  </div>

  <div className="border rounded p-4 dark:border-neutral-700 dark:bg-neutral-900">
    <div className="text-sm opacity-70">Sessions</div>
    <div className="text-2xl font-bold">{totals.sessions}</div>
  </div>

  <div className="border rounded p-4 dark:border-neutral-700 dark:bg-neutral-900">
    <div className="text-sm opacity-70">Users total</div>
    <div className="text-2xl font-bold">{totals.users}</div>
  </div>

  <div className="border rounded p-4 dark:border-neutral-700 dark:bg-neutral-900">
    <div className="text-sm opacity-70">Identified users</div>
    <div className="text-2xl font-bold">{totals.users_identified}</div>
  </div>

  <div className="border rounded p-4 dark:border-neutral-700 dark:bg-neutral-900">
    <div className="text-sm opacity-70">Anonymous users</div>
    <div className="text-2xl font-bold">{totals.users_anonymous}</div>
  </div>
</div>

      {/* Chart */}
      <div className="border rounded p-4 h-[340px] dark:border-neutral-700 dark:bg-neutral-900">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold">Events per day</div>
          {loading && <div className="text-sm opacity-70">Loading...</div>}
        </div>

        <ResponsiveContainer width="100%" height="100%">
  <LineChart data={daily}>
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis dataKey="event_date" />
    <YAxis />
    <Tooltip />
    <Legend />
    <Line type="monotone" dataKey="events" name="Events" strokeWidth={2} dot={false} />
    <Line type="monotone" dataKey="sessions" name="Sessions" strokeWidth={2} dot={false} />
    <Line type="monotone" dataKey="users" name="Users total" strokeWidth={2} dot={false} />
    <Line type="monotone" dataKey="users_identified" name="Identified" strokeWidth={2} dot={false} />
    <Line type="monotone" dataKey="users_anonymous" name="Anonymous" strokeWidth={2} dot={false} />
  </LineChart>
</ResponsiveContainer>
      </div>

      {/* Pages per day */}
<div className="border rounded p-4 h-[340px] dark:border-neutral-700 dark:bg-neutral-900">
  <div className="flex items-center justify-between mb-2">
    <div className="font-semibold">Page events per day</div>
    {loading && <div className="text-sm opacity-70">Loading...</div>}
  </div>

  <ResponsiveContainer width="100%" height="100%">
  <LineChart data={pagesDaily}>
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis dataKey="event_date" />
    <YAxis />
    <Tooltip />
    <Legend />
    <Line type="monotone" dataKey="events" name="Events" strokeWidth={2} dot={false} />
    <Line type="monotone" dataKey="sessions" name="Sessions" strokeWidth={2} dot={false} />
    <Line type="monotone" dataKey="users" name="Users total" strokeWidth={2} dot={false} />
    <Line type="monotone" dataKey="users_identified" name="Identified" strokeWidth={2} dot={false} />
    <Line type="monotone" dataKey="users_anonymous" name="Anonymous" strokeWidth={2} dot={false} />
  </LineChart>
</ResponsiveContainer>
</div>

{/* Top pages */}
<div className="border rounded p-4 dark:border-neutral-700 dark:bg-neutral-900">
  <div className="font-semibold mb-3">Top pages</div>
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead className="text-left opacity-70">
        <tr>
          <th className="py-2 pr-4">page_path</th>
          <th className="py-2 pr-4">surface</th>
          <th className="py-2 pr-4">project_slug</th>
          <th className="py-2 pr-4">events</th>
          <th className="py-2 pr-4">sessions</th>
          <th className="py-2 pr-4">users total</th>
          <th className="py-2 pr-4">identified</th>
          <th className="py-2 pr-4">anonymous</th>
        </tr>
      </thead>
      <tbody>
        {pagesTop.map((r, idx) => (
          <tr key={`${r.page_path}-${r.surface}-${r.project_slug}-${idx}`} className="border-t dark:border-neutral-800">
            <td className="py-2 pr-4">{r.page_path}</td>
            <td className="py-2 pr-4">{r.surface}</td>
            <td className="py-2 pr-4">{r.project_slug}</td>
            <td className="py-2 pr-4">{r.events}</td>
            <td className="py-2 pr-4">{r.sessions}</td>
            <td className="py-2 pr-4">{Number(r.users || 0)}</td>
            <td className="py-2 pr-4">{Number(r.users_identified || 0)}</td>
            <td className="py-2 pr-4">{Number(r.users_anonymous || 0)}</td>
          </tr>
        ))}
        {!pagesTop.length && (
          <tr><td className="py-3 opacity-70" colSpan={8}>No data</td></tr>
        )}
      </tbody>
    </table>
  </div>
</div>

      {/* Table by event_name */}
      <div className="border rounded p-4 dark:border-neutral-700 dark:bg-neutral-900">
        <div className="font-semibold mb-3">Top events</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left opacity-70">
              <tr>
  <th className="py-2 pr-4">event_name</th>
  <th className="py-2 pr-4">events</th>
  <th className="py-2 pr-4">sessions</th>
  <th className="py-2 pr-4">users total</th>
  <th className="py-2 pr-4">identified</th>
  <th className="py-2 pr-4">anonymous</th>
</tr>
            </thead>
            <tbody>
              {byName.map((r) => (
  <tr key={r.event_name} className="border-t dark:border-neutral-800">
    <td className="py-2 pr-4">{r.event_name}</td>
    <td className="py-2 pr-4">{Number(r.events || 0)}</td>
    <td className="py-2 pr-4">{Number(r.sessions || 0)}</td>
    <td className="py-2 pr-4">{Number(r.users || 0)}</td>
    <td className="py-2 pr-4">{Number(r.users_identified || 0)}</td>
    <td className="py-2 pr-4">{Number(r.users_anonymous || 0)}</td>
  </tr>
))}
{!byName.length && (
  <tr><td className="py-3 opacity-70" colSpan={6}>No data</td></tr>
)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}